import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import {
  afterNextRender,
  ApplicationRef,
  ErrorHandler,
  Injectable,
  Injector,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import {
  deleteToken,
  getToken,
  isSupported as isFirebaseMessagingSupported,
  Messaging,
  onMessage,
} from '@angular/fire/messaging';
import { NavigationEnd, Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import type { AppState } from '@capacitor/app';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import type { ActionPerformed as LocalNotificationActionPerformed } from '@capacitor/local-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import type {
  ActionPerformed as PushNotificationActionPerformed,
  Channel,
  PushNotificationSchema,
  Token,
} from '@capacitor/push-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { AppService } from '@freelancer/app';
import { Auth } from '@freelancer/auth';
import { isWebsocketMessage, WebSocketService } from '@freelancer/datastore';
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type {
  BaseServerMessage,
  WebsocketServerEvent,
  WebsocketServerEventTParam,
} from '@freelancer/datastore/core';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { LocalStorage } from '@freelancer/local-storage';
import { Location } from '@freelancer/location';
import { NativeSettingsService } from '@freelancer/native-settings';
import { Pwa } from '@freelancer/pwa';
import { Tracking } from '@freelancer/tracking';
import type { TrackingExtraParams } from '@freelancer/tracking/interface';
import {
  permissionReason,
  Permissions,
  PermissionStateEnum,
  PermissionType,
} from '@freelancer/ui/permissions';
import { ToastAlertService } from '@freelancer/ui/toast-alert';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { toNumber } from '@freelancer/utils';
import { FL_CALL_KIT_PLUGIN_NAME, FLCallkit } from '@freelancer/videochat';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  combineLatest,
  EMPTY,
  firstValueFrom,
  from,
  Observable,
  of,
  race,
  ReplaySubject,
  Subscription,
  timer,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import {
  BACKGROUND_NOTIFICATION_PLUGIN_NAME,
  BackgroundNotification,
  CUSTOM_NOTIFICATION_PLUGIN_NAME,
  CustomNotification,
} from './capacitor-plugins';

export type NotificationPermission = 'granted' | 'denied' | 'default';

export type NotificationRegisterResult =
  | {
      readonly status: 'success';
    }
  | {
      readonly status: 'error';
      readonly errorCode: NotificationPermissionErrorCode;
      // T249142: Required to capture what the problem we're having with
      // some users not registering push notifications correctly.
      readonly errorMsg?: string;
    };

export type NotificationRequestResult =
  | {
      readonly status: 'success';
    }
  | {
      readonly status: 'error';
      readonly errorCode: NotificationPermissionErrorCode.PERMISSION_DENIED;
    };

export enum NotificationPermissionErrorCode {
  //  Permission has been denied by the user
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  TOKEN_REQUEST_ERROR = 'TOKEN_REQUEST_ERROR',
}

interface Notification {
  type: 'push' | 'local';
  data?: {
    url?: string;
    resource_id?: string;
    push_notification_type?: string;
  };
}

export type PushNotificationTrackingExtraParams = Partial<
  Pick<
    TrackingExtraParams,
    'pushNotificationType' | 'url' | 'resourceId' | 'clicked'
  >
>;

const TRACK_CLICKED_NOTIF_EVENT_NAME = 'clicked_notification';
const TRACK_DELIVERED_NOTIF_EVENT_NAME = 'delivered_notification';
const TRACK_DELIVERED_BACKGROUND_NOTIF_EVENT_NAME =
  'delivered_background_notification';
const TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME = 'native_push_notifications';

/**
 * List of ignored notifications that should not be hydrated from the background.
 * These notifications are time sensitive and shouldn't be playback.
 *
 * /^video:/ video chat related time sensitive notifications
 */
const IGNORED_BACKGROUND_NOTIFICATIONS = /^video:/;

@UntilDestroy({ className: 'Notifications' })
@Injectable({ providedIn: 'root' })
export class Notifications implements OnDestroy {
  private auth = inject(Auth);
  private errorHandler = inject(ErrorHandler);
  private freelancerHttp = inject(FreelancerHttp);
  private localStorage = inject(LocalStorage);
  private location = inject(Location);
  private permissions = inject(Permissions);
  private push = inject(SwPush);
  private pwa = inject(Pwa);
  private tracking = inject(Tracking);
  private websocket = inject(WebSocketService);
  private router = inject(Router);
  private app = inject(AppService);
  private appRef = inject(ApplicationRef);
  private toastAlertService = inject(ToastAlertService);
  private nativeSettings = inject(NativeSettingsService);
  private platformId = inject(PLATFORM_ID);
  private uiConfig = inject<UiConfig>(UI_CONFIG);
  private injector = inject(Injector);

  tokenChanges$: Observable<string | undefined>;

  private readonly APP_STABLE_WAIT_TIMEOUT_MS = 5000;

  // Unsubscribe when unregistering the notification token
  private registerNotificationSubscriptions = new Subscription();
  // Unsubscribe when destroying the service
  private subscriptions = new Subscription();

  private registerNotificationListeners: PluginListenerHandle[] = [];
  private afMessaging?: Messaging;

  private permissionChangeListener?: (e: Event) => Promise<void>;

  private nativePermissionStateSubject$ =
    new ReplaySubject<NotificationPermission>(1);
  private nativePermissionState$ =
    this.nativePermissionStateSubject$.asObservable();

  private browserPermissionStateSubject$ = new ReplaySubject<PermissionState>(
    1,
  );
  private browserPermissionState$ =
    this.browserPermissionStateSubject$.asObservable();

  private internalPermissionState$ = this.localStorage
    .get('notificationsInternalGranted')
    .pipe(map(state => !!state));

  constructor() {
    this.init();
  }

  async init(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      // Checking if Messaging is supported before injecting it
      // https://stackoverflow.com/q/75712625
      const firebaseMessagingSupported = await isFirebaseMessagingSupported();
      if (firebaseMessagingSupported) {
        this.afMessaging = this.injector.get(Messaging);
      }

      if (!this.isSupported()) {
        return;
      }

      const notificationInternalGranted = await firstValueFrom(
        this.localStorage
          .get('notificationsInternalGranted')
          .pipe(untilDestroyed(this)),
      );
      if (notificationInternalGranted === undefined) {
        // Backfill and set the default internal permission state
        const permission = await this.getPermission();
        if (permission === 'granted') {
          await this.localStorage.set('notificationsInternalGranted', true);
        } else {
          await this.localStorage.set('notificationsInternalGranted', false);
        }
      }
    }
  }

  async ngOnDestroy(): Promise<void> {
    if (this.permissionChangeListener) {
      const handlers = await this.permissions.getPermissionHandlers();
      const windowRes =
        await handlers[PermissionType.PUSH_NOTIFICATIONS].webCheck();
      windowRes.removeEventListener('change', this.permissionChangeListener);
    }
    this.subscriptions.unsubscribe();
    this.registerNotificationSubscriptions.unsubscribe();
  }

  /**
   * Can permission to show notifications be requested. This implies that
   * permission has not been already granted.
   *
   * Used to display a user prompt to enable notifications.
   *
   * Dashboard-gating is the caller's responsibility: If a caller wants the prompt to fire on `/dashboard`,
   * it should await `waitUntilOnDashboard()` around the actual `requestNotificationPermission()` call.
   *
   * @param options - Options to control the permission request
   * - `skipIfRequested`: When set, return false if it has already been requested.
   */
  async canRequestPermissionInternal(
    options: { skipIfRequested?: boolean } = { skipIfRequested: false },
  ): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    if (options.skipIfRequested) {
      const requested = await firstValueFrom(
        this.localStorage
          .get('notificationsRequested')
          .pipe(untilDestroyed(this)),
      );
      if (requested) {
        return false;
      }
    }

    const permission = await this.getPermission();
    if (permission === 'default') {
      return true;
    }

    // For non-native users, if the notification permission is granted in browser settings,
    // we still need request permission when the user hasn't granted it internally
    if (permission === 'granted' && !this.pwa.isNative()) {
      const internalGranted = await firstValueFrom(
        this.internalPermissionState$.pipe(untilDestroyed(this)),
      );
      if (!internalGranted) {
        return true;
      }
    }

    // We always request permission for native by redirecting the user to the Settings app
    if (this.pwa.isNative()) {
      return true;
    }
    return false;
  }

  /**
   * Check whether the user is eligible to have their FCM token registered.
   *
   * Reports eligibility only — timing (e.g. throttling re-registration to
   * `/dashboard` to avoid overwhelming the backend) is the caller's
   * responsibility.
   *
   * @param requestResult If set, consider the internal permission request result. (browser permission might not be granted yet)
   * @param skipIfRegistered When set, return false if the user has already registered the notification
   */
  protected async canRegisterPushNotificationsInternal({
    requestResult,
    skipIfRegistered = true,
  }: {
    requestResult: NotificationRegisterResult | undefined;
    skipIfRegistered: boolean;
  }): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    const registered = await firstValueFrom(
      this.localStorage
        .get('notificationsRegistered')
        .pipe(untilDestroyed(this)),
    );

    // Check if the user has already registered the notification
    if (skipIfRegistered) {
      if (registered) {
        return false;
      }
    }

    // Always register the token for the native apps
    if (this.pwa.isNative()) {
      return true;
    }

    // Register the notification permission on web if it is granted
    // - hasGrantedPermission: the user has granted permission
    // - requestResult: user accepted permission from the internal modal
    //    in the next step `registerNotification` will request the browser notification permission
    return (
      (await this.hasGrantedPermission()) || requestResult?.status === 'success'
    );
  }

  /**
   * Disable notification permission
   * UnregisterFcmPushNotifications if possible
   */
  async disableAndUnregisterNotification(): Promise<void> {
    await this.localStorage.set('notificationsInternalGranted', false);

    if (this.pwa.isNative()) {
      this.nativeSettings.openNotificationSettings();
      return;
    }

    // Do not unregister if the user is using the native app.
    // Token is still used in sending the background notification.
    // Only unregister native tokens on logout.
    this.unregisterFcmPushNotifications({ skipNative: true });
  }

  /**
   * Request notification permission, and register the FCM token to our backend if possible
   * For native
   * - Register the FCM token to the backend even if the user has not granted permission.
   * For browser
   * - Only register the FCM token if the user has granted permission (both internal and browser).
   *
   * @param options
   * - `mustOnDashboard`: If true, only allow toggling permission if the user is on the dashboard.
   * - `skipIfRequested`: If true, skip the permission request if it has already been requested.
   * - `skipIfRegistered`: If true, skip the registration if the user has already registered the notification.
   * - `showAlert`: If true, when the permission is disabled in the browser show an alert since there is nothing we can do.
   */
  async enableAndRegisterNotification(options: {
    mustOnDashboard: boolean;
    skipIfRequested: boolean;
    skipIfRegistered: boolean;
    showAlert?: boolean;
  }): Promise<{
    requestResult?: NotificationRequestResult;
    registrationResult?: NotificationRegisterResult;
  }> {
    return this.pwa.isNative()
      ? this.enableAndRegisterNotificationNative(options)
      : this.enableAndRegisterNotificationBrowser(options);
  }

  /**
   * Native path: FCM registration is dashboard-independent — register once per
   * auth so background notifications work as soon as possible.
   */
  private async enableAndRegisterNotificationNative(options: {
    mustOnDashboard: boolean;
    skipIfRequested: boolean;
    skipIfRegistered: boolean;
    showAlert?: boolean;
  }): Promise<{
    requestResult?: NotificationRequestResult;
    registrationResult?: NotificationRegisterResult;
  }> {
    let requestResult: NotificationRequestResult | undefined;
    let registrationResult: NotificationRegisterResult | undefined;

    const canRegister = await this.canRegisterPushNotificationsInternal({
      requestResult: undefined,
      skipIfRegistered: options.skipIfRegistered,
    });
    if (canRegister) {
      registrationResult = await this.registerFcmNotificationToken();
      this.logIfUnexpectedRegistrationError(registrationResult);
    }

    if (options.mustOnDashboard) {
      await this.waitUntilOnDashboard();
      await this.waitUntilAppStable();
    }

    const canRequestNotification = await this.canRequestPermissionInternal({
      skipIfRequested: options.skipIfRequested,
    });
    if (canRequestNotification) {
      requestResult = await this.requestNotificationPermission();
    } else if (this.isSupported()) {
      const permission = await this.getPermission();
      // Nothing we can do if the permission is already denied. Let the user change the permission from the system level.
      if (permission === 'denied' && options.showAlert) {
        this.showNotificationsSettingsPrompt();
      }
    }

    return {
      requestResult,
      registrationResult,
    };
  }

  /**
   * Browser path: registration depends on permission grant, so it runs after
   * the modal prompt.
   */
  private async enableAndRegisterNotificationBrowser(options: {
    mustOnDashboard: boolean;
    skipIfRequested: boolean;
    skipIfRegistered: boolean;
    showAlert?: boolean;
  }): Promise<{
    requestResult?: NotificationRequestResult;
    registrationResult?: NotificationRegisterResult;
  }> {
    let requestResult: NotificationRequestResult | undefined;
    let registrationResult: NotificationRegisterResult | undefined;
    let onDashboard = false;

    const canRequestNotification = await this.canRequestPermissionInternal({
      skipIfRequested: options.skipIfRequested,
    });
    if (canRequestNotification) {
      if (options.mustOnDashboard) {
        await this.waitUntilOnDashboard();
        await this.waitUntilAppStable();
        onDashboard = true;
      }
      requestResult = await this.requestNotificationPermission();
    } else if (this.isSupported()) {
      const permission = await this.getPermission();
      // Nothing we can do if the permission is already denied. Let the user change the permission from the system level.
      if (permission === 'denied' && options.showAlert) {
        if (options.mustOnDashboard) {
          await this.waitUntilOnDashboard();
          await this.waitUntilAppStable();
          onDashboard = true;
        }
        this.showNotificationsSettingsPrompt();
      }
    }

    const canRegister = await this.canRegisterPushNotificationsInternal({
      requestResult,
      skipIfRegistered: options.skipIfRegistered,
    });
    if (canRegister) {
      if (options.mustOnDashboard && !onDashboard) {
        await this.waitUntilOnDashboard();
      }
      registrationResult = await this.registerFcmNotificationToken();
      this.logIfUnexpectedRegistrationError(registrationResult);
    }

    return {
      requestResult,
      registrationResult,
    };
  }

  /**
   * Wait until the router lands on `/dashboard`. Callers use this before
   * showing the permission prompt so it doesn't fire on intermediate URLs
   * (`/internal/auth/callback`, `/new-freelancer/*`, deep links, …). Returns
   * immediately if we're already on `/dashboard`.
   */
  private async waitUntilOnDashboard(): Promise<void> {
    if (this.router.url.startsWith('/dashboard')) {
      return;
    }
    await firstValueFrom(
      this.router.events.pipe(
        filter(
          (event): event is NavigationEnd =>
            event instanceof NavigationEnd &&
            event.urlAfterRedirects.startsWith('/dashboard'),
        ),
        untilDestroyed(this),
      ),
    );
  }

  /**
   * Wait until the app looks quiet enough to show a prompt.
   */
  private async waitUntilAppStable(): Promise<void> {
    await firstValueFrom(
      race(
        this.appRef.isStable.pipe(filter(stable => stable)),
        // Cap wait at 5S
        timer(this.APP_STABLE_WAIT_TIMEOUT_MS),
      ).pipe(untilDestroyed(this)),
    );
    await new Promise<void>(resolve => {
      afterNextRender(() => resolve(), { injector: this.injector });
    });
  }

  private logIfUnexpectedRegistrationError(
    result: NotificationRegisterResult,
  ): void {
    if (
      result.status === 'error' &&
      result.errorCode !== NotificationPermissionErrorCode.PERMISSION_DENIED
    ) {
      // Log the error as this should not happen as we are checking
      // canRegisterPushNotificationsInternal() first
      const errorMsgString = result.errorMsg ? `, ${result.errorMsg}` : '';
      this.errorHandler.handleError(
        new Error(
          `registerPushNotifications failed with code ${result.errorCode}${errorMsgString}`,
        ),
      );
    }
  }

  /**
   * Check whether the user has granted permission to show notifications.
   * - For native,
   *    it is checking whether the user has granted the permission in the Settings app.
   * - For browser
   *   it is checking the browser permission state and the internal permission state are both granted.
   */
  async hasGrantedPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }
    const permission = await this.getPermission();
    const hasGranted = permission === 'granted';
    if (this.pwa.isNative()) {
      return hasGranted;
    }

    // In the browser, we also need to check if the user has granted the permission internally
    const internalPermissionGranted = await firstValueFrom(
      this.internalPermissionState$.pipe(untilDestroyed(this)),
    );
    return hasGranted && internalPermissionGranted;
  }

  /**
   * Check whether the user has granted permission to show notifications.
   * - For native,
   *    it is checking whether the user has granted the permission in the Settings app.
   * - For browser
   *   it is checking the browser permission state and the internal permission state are both granted.
   */
  hasGrantedPermission$(): Observable<boolean> {
    if (this.pwa.isNative()) {
      return this.nativePermissionState$.pipe(
        map(state => state === 'granted'),
      );
    }

    return combineLatest([
      this.browserPermissionState$,
      this.internalPermissionState$,
    ]).pipe(
      map(([browserState, internalGranted]) => {
        return browserState === 'granted' && internalGranted;
      }),
    );
  }

  /** Whether the user has turned off notifications for the app in their device settings. */
  isDevicePermissionDenied$(): Observable<boolean> {
    if (!this.pwa.isNative() || !this.isSupported()) {
      return of(false);
    }

    return this.hasGrantedPermission$().pipe(
      map(granted => !granted),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  hasFcmNotificationRegistered(): Observable<boolean> {
    return this.localStorage
      .get('notificationsRegistered')
      .pipe(map(registered => !!registered));
  }

  /**
   * Are notifications supported by the platform.
   *
   * If this is true but both canRequestPermissionInternal() and hasGrantedPermission()
   * are false, this implies the user has previously denied permission: use
   * that a display a user prompt to try to recover the user.
   */
  isSupported(): boolean {
    // Disable on the server
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    // Notifications are always supported on native
    if (this.pwa.isNative()) {
      return true;
    }

    // Don't support on Enterprise sites for now
    if (this.uiConfig.theme) {
      return false;
    }

    // In the browser, both Service Workers & the Push API need to be supported
    // Technically we don't use the Angular Service Worker but using
    // `SwPush::isEnabled` here allows to share the SW enabling logic.
    // Service Worker is not enabled for localhost, but notifications will still work.
    return (
      (this.push.isEnabled || this.location.hostname === 'localhost') &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Register the device token to our backend to receive VOIP push notifications for iOS.
   * To receive a VOIP push notification does not require the user to grant notification permissions
   */
  async registerIosVOIPPushNotification(): Promise<void> {
    if (this.pwa.isNative()) {
      if (Capacitor.isPluginAvailable(FL_CALL_KIT_PLUGIN_NAME)) {
        // Only allow register VOIP token permission if the user is on the dashboard
        const userOnDashboard = this.router.url.startsWith('/dashboard');
        const hasRegistered = await firstValueFrom(
          this.localStorage
            .get('voipNotificationsRegistered')
            .pipe(untilDestroyed(this)),
        );
        // Only allow re-register VOIP token if the user is on the dashboard
        if (!userOnDashboard && hasRegistered === true) {
          return;
        }

        const { deviceToken } = await FLCallkit.getVOIPDeviceToken();
        const deviceId = await this.getDeviceId();
        const userId = await firstValueFrom(
          this.auth.getUserId().pipe(untilDestroyed(this)),
        );

        this.registerNotificationSubscriptions.add(
          this.freelancerHttp
            .post('users/0.1/tokens', {
              kind: 'ios',
              app_type: 'freelancer',
              data: deviceToken,
              device_id: deviceId,
              voip_token: deviceToken,
              user_id: toNumber(userId),
            })
            .subscribe({
              next: () => {
                this.localStorage.set('voipNotificationsRegistered', true);
              },
              error: err => {
                this.errorHandler.handleError(err);
              },
            }),
        );

        // Unregister voip token on log out
        this.auth.registerBeforeLogoutAction(async () => {
          await this.localStorage.set('voipNotificationsRegistered', false);
          if (deviceToken.length === 0) {
            return;
          }
          await firstValueFrom(
            this.freelancerHttp
              .delete('users/0.1/tokens', {
                params: {
                  kind: 'ios',
                  app_type: 'freelancer',
                  data: deviceToken,
                },
              })
              .pipe(untilDestroyed(this)),
          );
        });
      }
    }
  }

  /**
   * Registers the push token with the backend
   * - In webapp, if browser notification permission is not granted, it will request the permission.
   * - In native, it won't request the permission
   */
  async registerFcmNotificationToken(): Promise<NotificationRegisterResult> {
    if (this.pwa.isNative()) {
      // We need to use a replay subject here to cache the registration result until tokenChanges$ is subscribed to
      const registrationSubject$ = new ReplaySubject<string | undefined>(1);
      const successListener = await PushNotifications.addListener(
        'registration',
        (t: Token) => {
          registrationSubject$.next(t.value);
        },
      );

      const errorListener = await PushNotifications.addListener(
        'registrationError',
        (e: { error: string }) => {
          registrationSubject$.error(e.error);
        },
      );
      this.registerNotificationListeners = [
        successListener,
        errorListener,
        ...this.registerNotificationListeners,
      ];

      this.tokenChanges$ = from(
        PushNotifications.register().catch(error =>
          registrationSubject$.error(error),
        ),
      ).pipe(switchMap(() => registrationSubject$.asObservable()));
    } else {
      // non-native handling
      this.tokenChanges$ = from(
        navigator.serviceWorker
          .register('firebase-messaging-sw.js', {
            scope: 'firebase-cloud-messaging-push-scope',
          })
          .then(swRegistration => {
            if (!this.afMessaging) {
              return undefined;
            }
            // getToken also request the permission if it is not granted yet.
            return getToken(this.afMessaging, {
              serviceWorkerRegistration: swRegistration,
            });
          }),
      ).pipe(map(token => token ?? undefined));
    }

    const permissionGranted = await this.hasGrantedPermission();

    const result = await firstValueFrom(
      this.tokenChanges$.pipe(untilDestroyed(this)),
    )
      .then(() => ({ status: 'success' as const }))
      .catch(e => {
        if (!permissionGranted) {
          return {
            status: 'error' as const,
            errorCode: NotificationPermissionErrorCode.PERMISSION_DENIED,
          };
        }
        return {
          status: 'error' as const,
          errorCode: NotificationPermissionErrorCode.TOKEN_REQUEST_ERROR,
          errorMsg: e?.toString(),
        };
      });

    if (result.status !== 'success') {
      return result;
    }

    const deviceId = await this.getDeviceId();
    const isNative = this.pwa.isNative();

    this.registerNotificationSubscriptions.add(
      this.tokenChanges$
        .pipe(
          switchMap(token =>
            this.auth.getUserId().pipe(
              take(1),
              switchMap(userId =>
                this.freelancerHttp.post('users/0.1/tokens', {
                  kind: 'fcm',
                  app_type: 'freelancer',
                  data: token,
                  device_id: deviceId,
                  user_id: toNumber(userId),
                  ...(isNative && {
                    is_native: true,
                  }),
                }),
              ),
            ),
          ),
        )
        .subscribe({
          next: async () => {
            await this.localStorage.set('notificationsRegistered', true);
          },
          error: err => {
            this.errorHandler.handleError(err);
          },
        }),
    );

    // Unregister fcm token on log out
    this.auth.registerBeforeLogoutAction(async () => {
      this.localStorage.set('notificationsRequested', false);

      await this.unregisterFcmPushNotifications();
    });

    return result;
  }

  /**
   * Unregister the application to receive push notifications.
   * @param option
   * - `skipNative`: If true, skip the native unregistration.
   *       Native token is useful for background notifications. Do not unregister it when the notification permission is disabled.
   */
  async unregisterFcmPushNotifications(
    option: { skipNative?: boolean } = { skipNative: false },
  ): Promise<void> {
    // Reset subscriptions
    this.registerNotificationSubscriptions.unsubscribe();
    this.registerNotificationSubscriptions = new Subscription();

    for (const listener of this.registerNotificationListeners) {
      listener.remove();
    }
    this.registerNotificationListeners = [];

    await this.localStorage.set('notificationsRegistered', false);

    // Silently return if notifications haven't been setup
    if (!this.tokenChanges$) {
      return;
    }

    if (option.skipNative && this.pwa.isNative()) {
      return;
    }

    return firstValueFrom(
      this.tokenChanges$.pipe(
        switchMap(token => {
          if (!token) {
            return EMPTY;
          }
          return combineLatest([
            this.freelancerHttp.delete('users/0.1/tokens', {
              params: {
                kind: 'fcm',
                app_type: 'freelancer',
                data: token,
              },
            }),
            // if not native, delete token
            !this.pwa.isNative() && this.afMessaging
              ? deleteToken(this.afMessaging)
              : // otherwise ignore
                of(undefined),
          ]);
        }),

        map(() => undefined),
        untilDestroyed(this),
      ),
    );
  }

  /**
   * Register notification related listeners
   */
  async setupNotificationListeners(): Promise<void> {
    let foregroundMessages$: Observable<object>;
    // Foreground messages stream specifically for native.
    let nativeForegroundMessages$:
      | Observable<PushNotificationSchema>
      | undefined;
    let notificationClicks$: Observable<{
      action?: string;
      notification: Notification;
    }>;

    if (this.pwa.isNative()) {
      // Notification channel is for Android only. Android notifications have to
      // be bound to a channel in order for it to display with alert/toast style
      // and other configurations including lights/vibration.
      if (this.pwa.getPlatform() === 'android') {
        const newMessageChannel: Channel = {
          // This channel ID is also used as the default channel for firebase
          // messaging notifications, set via
          // `com.google.firebase.messaging.default_notification_channel_id`
          id: 'newMessageChannel',
          name: $localize`New Message`,
          description: $localize`New incoming messages`,
          importance: 5,
          sound: 'message.wav',
          visibility: 1,
          vibration: true,
          lights: true,
          lightColor: '#29B2FE',
        };
        PushNotifications.createChannel(newMessageChannel);
        LocalNotifications.createChannel(newMessageChannel);
      }

      this.removeNotificationsOnActiveState();

      // Set listener to clear notifications everytime user switches back to the app
      this.subscriptions.add(
        this.app.appState$.subscribe(async state => {
          if (state.isActive) {
            await this.trackDeliveredNotifications();
            PushNotifications.removeAllDeliveredNotifications();
            this.handleBackgroundNotifications();
          }
        }),
      );

      // Clear the notifications when the user lands on the inbox.
      this.subscriptions.add(
        this.location
          .valueChanges()
          .pipe(filter(location => location.pathname.startsWith('/messages')))
          .subscribe(_ => {
            this.removeNotificationsOnActiveState();
          }),
      );

      nativeForegroundMessages$ = new Observable(observer => {
        const listener = PushNotifications.addListener(
          'pushNotificationReceived',
          (notification: PushNotificationSchema) => {
            observer.next(notification);
          },
        );
        return async () => (await listener).remove();
      });
      foregroundMessages$ = nativeForegroundMessages$;
      // New messages that comes through WebSocket don't pop up in the UI on
      // Android and iOS. Hence, we create local notifications to alert the user.
      this.subscriptions.add(
        nativeForegroundMessages$.subscribe(async notification => {
          if (notification.body && notification.data.url) {
            // Strip the query params from the relative URL in the notification.
            const redirectURL = notification.data.url.replace(/\?.*$/, '');
            // Ignore notification if we are already in the message thread.
            if (redirectURL !== this.location.pathname) {
              // Forward the push notification to local notification.
              LocalNotifications.schedule({
                notifications: [
                  {
                    // Generate a random ID.
                    id: Math.floor((Math.random() + 1) * 0x10_00_00_00),
                    title: notification.title ?? $localize`New Message`,
                    body: notification.body,
                    // This set the sound file for iOS.
                    // Android sound file is set via the channel.
                    sound: 'message.wav',
                    channelId: 'newMessageChannel',
                    extra: {
                      data: notification.data,
                    },
                  },
                ],
              });
            }
          }
        }),
      );

      notificationClicks$ = new Observable(observer => {
        const pushNotificationsListener = PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (actionPerformed: PushNotificationActionPerformed) => {
            observer.next({
              action: actionPerformed.actionId,
              notification: {
                type: 'push',
                data: actionPerformed.notification.data,
              },
            });
          },
        );

        const localNotificationsListener = LocalNotifications.addListener(
          'localNotificationActionPerformed',
          (actionPerformed: LocalNotificationActionPerformed) => {
            observer.next({
              action: actionPerformed.actionId,
              notification: {
                type: 'local',
                data: actionPerformed.notification.extra.data,
              },
            });
          },
        );

        return async () => {
          (await pushNotificationsListener).remove();
          (await localNotificationsListener).remove();
        };
      });
    } else {
      // non-native handling
      foregroundMessages$ = new Observable(sub => {
        if (!this.afMessaging) {
          return;
        }
        return onMessage(this.afMessaging, msg => sub.next(msg));
      });

      notificationClicks$ = new Observable(observer => {
        const handler = (event: any): void => {
          const internalPayload = event.data;

          if (!internalPayload.firebaseMessaging) {
            return;
          }

          if (internalPayload.firebaseMessaging.payload) {
            const payload = internalPayload.firebaseMessaging.payload.data;

            if (
              this.location.searchParams.has('recordNotifications') &&
              internalPayload.firebaseMessaging.type === 'push-received'
            ) {
              // Mark the message as received (for E2E tests)
              const payloadJSON = JSON.stringify(payload);
              if (!payloadJSON.includes('[e2e]')) {
                return;
              }

              const marker = 'fgPushMessage';
              const existingKids = Array.from(
                document.head.getElementsByClassName(marker),
              );

              // Add node marking message received
              const markerElement = document.createElement('div');
              markerElement.setAttribute('class', marker);
              const markerText = document.createTextNode(payloadJSON);
              markerElement.appendChild(markerText);
              document.head.appendChild(markerElement);

              // Remove previously-present nodes
              existingKids.forEach(childElement => {
                childElement.parentNode?.removeChild(childElement);
              });
            }

            if (
              internalPayload.firebaseMessaging.type === 'notification-clicked'
            ) {
              observer.next({
                // TODO: T267853 - implement custom actions when needed
                notification: {
                  ...internalPayload.firebaseMessaging.payload.notification,
                  data: payload,
                },
              });
            }
          }
        };
        navigator.serviceWorker.addEventListener('message', handler);
        return () => {
          navigator.serviceWorker.removeEventListener('message', handler);
        };
      });
    }
    await this.registerSystemPermissionChangeListener();

    this.subscriptions.add(
      foregroundMessages$.subscribe(notification => {
        // This is called when notifications are received while the app is in
        // foreground. Presumably we don't need to do anything here for
        // non-native environment as the same notifications will be received
        // through the WebSocket, which will be used to update the UI(pops up
        // the messaging thread) or show a toast notification.
        console.log('New foreground message: ', notification);
      }),
    );

    this.registerNotificationSubscriptions.add(
      notificationClicks$.subscribe(async ({ action, notification }) => {
        this.trackClickedNotification(notification);
        this.handleBackgroundNotifications();

        // This is called when the user performs an action on a notification
        console.log('Notification click: ', action, notification);
        // If the url data field is set, navigate to the destination
        const url = notification?.data?.url;

        // This is to avoid reloading the page after the PWA loader has
        // already navigated to the correct route.
        if (url && url !== this.location.href) {
          this.location.navigateByUrl(url, { replaceUrl: false });
        }
      }),
    );
  }

  /**
   * @deprecated
   * Manually trigger a notification
   */
  showNotification(): void {
    // We explicitly DO NOT want to implement that, i.e. create & show
    // Notifications from the webapp itself: all notifications should be
    // transformed/filtered/sent from the backend and are automatically handled
    // in the Service Worker/native background tasks in order to ensure
    // consistency & provide background notifications support accross platforms
    throw new Error('Not implemented on purpose');
  }

  /**
   * Remove notifications when user switches back to the app
   */
  async removeNotificationsOnActiveState(): Promise<void> {
    if (this.pwa.isNative()) {
      // Clear notifications on fresh open of the app
      await App.getState().then(async (state: AppState) => {
        if (state.isActive) {
          await this.trackDeliveredNotifications();
          PushNotifications.removeAllDeliveredNotifications();
          this.handleBackgroundNotifications();
        }
      });
    }
  }

  /*
   * Send tracking for delivered notifications.
   *
   * Note: does not include the notification that was clicked on
   */
  private async trackDeliveredNotifications(): Promise<void> {
    const deliveredNotifications =
      await PushNotifications.getDeliveredNotifications();
    deliveredNotifications.notifications.forEach(
      (notification: PushNotificationSchema) => {
        // Filter out the extra `group_summary` notification that is returned
        // here but is not actually a delivered push notification.
        if (!notification.groupSummary) {
          const extraParams: PushNotificationTrackingExtraParams = {};

          this.tracking.trackCustomEvent(
            TRACK_DELIVERED_NOTIF_EVENT_NAME,
            TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
            extraParams,
          );
        }
      },
    );
  }

  async handleBackgroundNotifications(): Promise<void> {
    const websockets = await this.getBackgroundNotificationAsWebSocket();
    if (websockets.length > 0) {
      const userId = await firstValueFrom(
        this.auth.getUserId().pipe(untilDestroyed(this)),
      );
      websockets.forEach(event => {
        if (
          // Converted websocket
          isWebsocketMessage<BaseServerMessage>(event) &&
          !event.body.type.match(IGNORED_BACKGROUND_NOTIFICATIONS)
        ) {
          this.websocket.processServerMessage(event, userId);
        }
      });

      // Track delivered background notifications
      const extraParams = {
        count: websockets.length,
      };
      this.tracking.trackCustomEvent(
        TRACK_DELIVERED_BACKGROUND_NOTIF_EVENT_NAME,
        TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
        extraParams,
      );
    }
    this.deleteBackgroundNotifications();
  }

  isBackgroundNotificationSupported(): boolean {
    return (
      this.pwa.isNative() &&
      Capacitor.isPluginAvailable(BACKGROUND_NOTIFICATION_PLUGIN_NAME)
    );
  }

  isCustomNotificationSupported(): boolean {
    return (
      this.pwa.isNative() &&
      Capacitor.isPluginAvailable(CUSTOM_NOTIFICATION_PLUGIN_NAME)
    );
  }

  getCustomNotificationPluginVersion(): Promise<string> {
    if (!this.isCustomNotificationSupported()) {
      throw new Error('Custom notification plugin is not supported');
    }
    return CustomNotification.pluginVersion().then(version => version.version);
  }

  /**
   * Get the background notifications from native and transform it as an WS message.
   */
  async getBackgroundNotificationAsWebSocket(): Promise<
    WebsocketServerEvent<WebsocketServerEventTParam>[]
  > {
    if (!this.isBackgroundNotificationSupported()) {
      return [];
    }

    const result = await BackgroundNotification.getBackgroundNotificationData();
    const ret: WebsocketServerEvent<WebsocketServerEventTParam>[] = [];
    result.data.forEach(data => {
      try {
        const dataObject = JSON.parse(data);
        if (dataObject.ParentType === 'messages') {
          ret.push({
            body: dataObject.Raw,
            channel: 'user',
          });
        }
      } catch (err) {
        this.errorHandler.handleError(err);
      }
    });

    return ret;
  }

  async deleteBackgroundNotifications(): Promise<void> {
    if (!this.isBackgroundNotificationSupported()) {
      return;
    }
    await BackgroundNotification.deleteDataFiles();
  }

  private showNotificationsSettingsPrompt(): void {
    if (this.pwa.isNative()) {
      this.toastAlertService.open({
        message: $localize`Please enable notifications in the Settings app.`,
      });
    } else {
      this.toastAlertService.open({
        message: $localize`Please enable notifications in your browser settings.`,
      });
    }
  }

  /*
   * Send tracking for a clicked notifications.
   *
   * Since `trackDeliveredNotifications()` does not include the clicked notification,
   * this function will also send a delivered tracking for this notification
   */
  private async trackClickedNotification(
    notification: Notification,
  ): Promise<void> {
    const extraParams: PushNotificationTrackingExtraParams = {};

    extraParams.clicked = 'true';

    if (notification.data?.url) {
      extraParams.url = notification.data?.url;
    }

    if (notification.data?.push_notification_type) {
      extraParams.pushNotificationType =
        notification.data.push_notification_type;
    }

    if (notification.data?.resource_id) {
      extraParams.resourceId = notification.data.resource_id;
    }

    this.tracking.trackCustomEvent(
      TRACK_CLICKED_NOTIF_EVENT_NAME,
      TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
      extraParams,
    );
    this.tracking.trackCustomEvent(
      TRACK_DELIVERED_NOTIF_EVENT_NAME,
      TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
    );
  }

  private async getPermission(): Promise<NotificationPermission> {
    if (this.pwa.isNative()) {
      return this.getPermissionNative();
    }

    return Notification.permission;
  }

  private async getPermissionNative(): Promise<NotificationPermission> {
    if (!this.pwa.isNative()) {
      throw new Error('This method is only available on native platforms');
    }

    const permission = await PushNotifications.checkPermissions();
    if (
      permission.receive === 'prompt' ||
      permission.receive === 'prompt-with-rationale'
    ) {
      return 'default';
    }
    return permission.receive;
  }

  private async getDeviceId(): Promise<string> {
    const deviceId = firstValueFrom(
      this.localStorage.get('deviceId').pipe(untilDestroyed(this)),
    );
    if (typeof deviceId === 'string' && deviceId) {
      return deviceId;
    }
    const id = uuidv4();
    await this.localStorage.set('deviceId', id);
    return id;
  }

  private async registerSystemPermissionChangeListener(): Promise<void> {
    // Observe the system permission state changes on app opens
    if (this.pwa.isNative()) {
      const initialState = await this.getPermissionNative();
      this.nativePermissionStateSubject$.next(initialState);
      this.subscriptions.add(
        this.app.appState$.subscribe(async state => {
          // When ever the app switch back to active, update the native permission state
          if (state.isActive) {
            const nativePermissionState = await this.getPermissionNative();
            this.nativePermissionStateSubject$.next(nativePermissionState);
            if (nativePermissionState === 'granted') {
              // If the permission is granted, register the FCM token if it is not already registered
              // Since we don't remove the FCM token on permission disabled, it is safe to assume that we already have it in the backend.
              const canRegister =
                await this.canRegisterPushNotificationsInternal({
                  requestResult: undefined,
                  skipIfRegistered: true,
                });
              if (canRegister) {
                this.registerFcmNotificationToken();
              }
            }
          }
        }),
      );
    } else {
      const handlers = await this.permissions.getPermissionHandlers();
      const permissionStatus =
        await handlers[PermissionType.PUSH_NOTIFICATIONS].webCheck();
      // init the browser permission state subject
      this.browserPermissionStateSubject$.next(permissionStatus.state);

      // Override the internal permission state on the browser permission state changes
      this.permissionChangeListener = async (e: Event) => {
        const { state } = e.target as PermissionStatus;
        if (state !== PermissionStateEnum.GRANTED) {
          await this.localStorage.set('notificationsInternalGranted', false);
          this.unregisterFcmPushNotifications();
        } else {
          await this.localStorage.set('notificationsInternalGranted', true);
          const canRegister = await this.canRegisterPushNotificationsInternal({
            requestResult: undefined,
            skipIfRegistered: true,
          });
          if (canRegister) {
            this.registerFcmNotificationToken();
          }
        }
        this.browserPermissionStateSubject$.next(state);
      };

      permissionStatus.addEventListener(
        'change',
        this.permissionChangeListener,
      );
    }
  }

  /**
   * Requests push notification permission from the user.
   */
  private async requestNotificationPermission(): Promise<NotificationRequestResult> {
    if (!this.isSupported()) {
      throw new Error(
        'Push notifications are not supported on the current platform: make sure to check isSupported() before calling requestNotificationPermission()',
      );
    }

    const isLoggedIn = await firstValueFrom(
      this.auth.isLoggedIn().pipe(untilDestroyed(this)),
    );
    if (!isLoggedIn) {
      throw new Error(
        'User must be logged in to request push notification permission',
      );
    }

    // For native
    // - Trigger the internal permission modal then the native request modal
    // For web
    // - trigger the internal permission modal.
    //   The browser permission request modal will be triggered in registerFcmNotificationToken
    const permissionResult = await this.permissions.requestPermissions({
      permission: PermissionType.PUSH_NOTIFICATIONS,
      reason: permissionReason.PUSH_NOTIFICATIONS,
    });
    await this.localStorage.set('notificationsRequested', true);
    if (!permissionResult) {
      return {
        status: 'error',
        errorCode: NotificationPermissionErrorCode.PERMISSION_DENIED,
      };
    }

    // Update the native permission state, as it should be updated after the permission request
    if (this.pwa.isNative()) {
      const nativePermissionState = await this.getPermissionNative();
      this.nativePermissionStateSubject$.next(nativePermissionState);
    } else {
      this.localStorage.set('notificationsInternalGranted', permissionResult);
    }

    return { status: 'success' };
  }
}
